import { FC, useState, useEffect } from "react";
import "./Panel.scss";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import IMAGES from "./Img";
import { useAppSelector } from "../../assets/hooks/hooks";
import { PanelItemChildren, PanelItems } from "../Root/Root";
import { AccessSubject, canAccess } from "../../access";
import ImageSprite from "../../assets/components/ImageSprite/ImageSprite";

type PanelProps = {
    items: PanelItems;
};

// Helper function to determine which group should be open based on pathname
// Automatically works with any expandable groups defined in items
const getOpenGroupFromPath = (
    pathname: string,
    items: PanelItems
): string | null => {
    for (const item of items) {
        if (item.children && pathname.includes(`/${item.path}`)) {
            return item.path;
        }
    }
    return null;
};

const Panel: FC<PanelProps> = ({ items }) => {
    const user = useAppSelector((state) => state.initializeApp);
    const navigate = useNavigate();
    const location = useLocation();

    const subject: AccessSubject = {
        role: user.role,
        permissions: user.permissions,
    };

    // A child without a rule of its own is gated by the group's rule.
    const accessibleChildren = (
        item: PanelItems[number]
    ): PanelItemChildren[] =>
        item.children?.filter((child) =>
            canAccess(subject, child.access ?? item.access)
        ) ?? [];

    // A group with no accessible child must stay hidden — clicking it would
    // navigate to a route the user cannot open.
    const isItemVisible = (item: PanelItems[number]): boolean => {
        if (!canAccess(subject, item.access)) return false;
        return item.children === undefined || accessibleChildren(item).length > 0;
    };

    // State to track which expandable group is currently open
    // Initialize based on current URL to prevent layout shift on page load
    const [openGroup, setOpenGroup] = useState<string | null>(() =>
        getOpenGroupFromPath(location.pathname, items)
    );

    // Determine which group should be open based on current route
    useEffect(() => {
        const newOpenGroup = getOpenGroupFromPath(location.pathname, items);

        // Only update state if the value is actually changing
        // This prevents unnecessary re-renders when navigating within the same group
        setOpenGroup((prev) => (newOpenGroup !== prev ? newOpenGroup : prev));
    }, [location.pathname, items]);

    const handleParentClick = (parentPath: string, firstChildPath: string) => {
        // Navigate to the first child of the group
        // This will trigger the useEffect which will open the correct group
        navigate(`${parentPath}/${firstChildPath}`);
    };

    const NavButton: FC<{ item: any; id: number }> = ({ item, id }) => (
        <NavLink
            tabIndex={id + 1}
            about={item.name}
            end={item.path === ""}
            to={item.path}
            className={({ isActive }) =>
                isActive ? "nav_button active" : "nav_button"
            }
        >
            <div className="nav_button__icon">
                <ImageSprite iconSprite={IMAGES[item.icon]} />
            </div>
            <div className="nav_button__text">{item.name}</div>
        </NavLink>
    );

    const SubMenu: FC<{ item: PanelItems[number] }> = ({ item }) => (
        <ul className="nav_submenu">
            {accessibleChildren(item).map((child) => (
                <li key={child.path} className="nav_panel__button">
                    <NavLink
                        to={`${item.path}/${child.path}`}
                        className={({ isActive }) =>
                            isActive ? "nav_button active" : "nav_button"
                        }
                    >
                        <div className="nav_button__text">{child.name}</div>
                    </NavLink>
                </li>
            ))}
        </ul>
    );

    return (
        <ul className="nav_panel">
            {items.map((item, id) =>
                isItemVisible(item) ? (
                    <li
                        key={item.name}
                        className={`nav_panel__button ${
                            item.children && "nav_panel__button_sub"
                        }`}
                    >
                        {item.children && item.children.length > 0 ? (
                            <>
                                <div
                                    className="nav_button"
                                    onClick={() =>
                                        handleParentClick(
                                            item.path,
                                            accessibleChildren(item)[0].path
                                        )
                                    }
                                >
                                    <div className="nav_button__icon">
                                        <ImageSprite
                                            iconSprite={IMAGES[item.icon]}
                                        />
                                    </div>
                                    <div className="nav_button__text">
                                        {item.name}
                                    </div>
                                </div>
                                {openGroup === item.path && (
                                    <SubMenu item={item} />
                                )}
                            </>
                        ) : (
                            <NavButton item={item} id={id} />
                        )}
                    </li>
                ) : null
            )}
        </ul>
    );
};

export default Panel;
